import {
  BlocksConfigContextType,
  useBlocksConfig,
  useEditorStateSelector,
  useViewerState
} from "@pathwright/blocks-engine"
import useScrollToAndHighlight from "@pathwright/ui/src/components/link/useScrollToAndHighlight"
import useScrollToHashWithHashObserver from "@pathwright/ui/src/components/link/useScrollToHashWithHashObserver"
import { useCallback, useRef } from "react"
import {
  BLOCK_LINK_HIGHLIGHT_CLASS,
  BLOCK_LINK_HIGHLIGHT_DURATION_MS,
  BLOCK_LINK_HIGHLIGHT_STYLE_ID
} from "./constants"
import { resolveBlockHashToElementId } from "./utils"

type NavigateToBlockElementOptions = {
  updateHash?: boolean
  scrollBehavior?: ScrollBehavior
  scrollBlock?: ScrollLogicalPosition
  highlightDurationMs?: number
}

/**
 * Imperatively update URL hash, scroll to, and highlight a block element by id.
 * Useful when we need same-route block navigation (no remount) while reusing
 * the same highlight class used by standard Blocks links.
 */
export function navigateToBlockElement(
  blockElementID: string,
  {
    updateHash = true,
    scrollBehavior = "smooth",
    scrollBlock = "start",
    highlightDurationMs = BLOCK_LINK_HIGHLIGHT_DURATION_MS
  }: NavigateToBlockElementOptions = {}
): HTMLElement | null {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return null
  }

  if (updateHash) {
    window.history.replaceState(null, "", `#${blockElementID}`)
  }

  const blockElement = document.getElementById(blockElementID)
  if (!blockElement) return null

  blockElement.scrollIntoView({
    behavior: scrollBehavior,
    block: scrollBlock
  })

  blockElement.classList.add(BLOCK_LINK_HIGHLIGHT_CLASS)
  window.setTimeout(() => {
    blockElement.classList.remove(BLOCK_LINK_HIGHLIGHT_CLASS)
  }, highlightDurationMs)

  return blockElement
}

// --- Blocks links hook (single entry for consumers) ---

const BLOCKS_VIEWER_SELECTOR_PREFIX = ".Step--is-active #blocks-content-viewer-"
const BLOCKS_VIEWER_SELECTOR_SUFFIX = " .BlockWrap"

/**
 * Turn on block links for this context: scroll to block on hash, optional hash observer.
 * - VIEW: observe blocks in view (update URL hash) + scroll to and highlight block on load/hash change.
 * - EDIT: scroll to and highlight block on load/hash change only (no observer).
 * Call once from ContentViewer (with mode from config) or ContentEditor (with "EDIT").
 */
export function useBlocksLinks(mode: BlocksConfigContextType["mode"]): void {
  const { contextKey } = useBlocksConfig()
  const editorBlocks = useEditorStateSelector((state) => state.blocks) ?? []
  const { blocks: viewerBlocks } = useViewerState()

  const blocks = mode === "VIEW" ? viewerBlocks : editorBlocks
  const blocksRef = useRef(blocks)
  blocksRef.current = blocks

  // Stable callback so scroll/hash effects don't re-run when blocks array reference
  // changes (e.g. viewer state update on input click). Resolving still uses latest blocks via ref.
  const resolveElementId = useCallback((hashId: string) => {
    return resolveBlockHashToElementId(hashId, blocksRef.current)
  }, [])

  const scrollOptions = {
    resolveElementId,
    highlightClass: BLOCK_LINK_HIGHLIGHT_CLASS,
    styleId: BLOCK_LINK_HIGHLIGHT_STYLE_ID
  }

  const selector = `${BLOCKS_VIEWER_SELECTOR_PREFIX}${contextKey}${BLOCKS_VIEWER_SELECTOR_SUFFIX}`
  useScrollToHashWithHashObserver({
    selector,
    shouldScroll: mode === "VIEW",
    enableObserver: mode === "VIEW",
    ...scrollOptions
  })

  useScrollToAndHighlight({
    scrollDuration: 700,
    offset: 100,
    autofocus: false,
    isActive: mode === "EDIT",
    ...scrollOptions
  })
}
